<script lang="ts">
import { useField } from 'vee-validate';
import { computed, defineComponent, InputHTMLAttributes, PropType, ref } from 'vue';

import WarningIcon from '@/components/base/assets/WarningIcon.vue';
import { useForm } from '@/composables/useForm';

export const borderRules = (invalid: boolean) => ({
  width: 'border',
  style: 'border-solid',
  color: invalid ? 'border-nuts-red-800' : 'border-neutral-300',
  focusColor: 'focus:border-black',
  focusOutline: 'focus:outline-none',
});

export const fontRules = ({
  invalid,
  showPlaceholder,
}: {
  invalid: boolean;
  showPlaceholder: boolean;
}) => {
  let placeholderColor;
  if (showPlaceholder) {
    if (invalid) placeholderColor = 'placeholder-red-700';
  } else {
    placeholderColor = 'placeholder-transparent';
  }

  return {
    letterSpacing: 'leading-4 md:leading-4',
    placeholderColor,
    fontSize: 'text-base',
    color: invalid ? 'text-nuts-red-800' : 'text-black',
  };
};

export const sizeRules = () => ({
  height: 'h-11 md:h-12',
  padding: 'px-4',
  width: 'w-full',
});

type RequiredAttributes = Required<Pick<InputHTMLAttributes, 'name' | 'placeholder'>>;
export type InputAttributes = InputHTMLAttributes &
  RequiredAttributes & { type?: 'date' | 'email' | 'number' | 'password' | 'tel' | 'text' };

export default defineComponent({
  name: 'FormInput',
  props: {
    autocomplete: { required: false, type: String },
    alternateBackground: { required: false, type: Boolean, default: false },
    dataTest: { required: false, type: String },
    error: { required: false, type: Boolean },
    helperText: { required: false, type: String },
    inputAttributes: { required: true, type: Object as PropType<InputAttributes> },
    modelValue: { required: true, type: [String, Number], default: '' },
    showLabel: { required: false, type: Boolean, default: false },
    showPlaceholder: { required: false, type: Boolean, default: false },
    suppressMessaging: { required: false, type: Boolean, default: false },
    theme: { required: false, type: String as PropType<'light' | 'dark'>, default: 'light' },
    validator: { required: false, type: Function },
  },
  components: { WarningIcon },
  emits: ['blur', 'focus'],
  setup(props, { emit }) {
    const { validatorFailed } = useForm();

    const inputName = ref(props.inputAttributes.name);
    const { errorMessage, handleBlur, meta, setErrors, value } = useField(
      inputName,
      props.validator,
      {
        initialValue: props.modelValue,
      },
    );

    const inputFailed = computed(() => validatorFailed(meta) || props.error);
    const attributes = computed<InputAttributes>(() => ({
      ...props.inputAttributes,
      type: props.inputAttributes.type ?? 'text',
    }));

    const getInputStyles = computed(() => {
      const styles = [];

      styles.push([
        Object.values(borderRules(inputFailed.value)).join(' '),
        Object.values(
          fontRules({ invalid: inputFailed.value, showPlaceholder: props.showPlaceholder }),
        ).join(' '),
        Object.values(sizeRules()).join(' '),
      ]);

      if (attributes.value.type === 'password' && value.value !== '' && value.value !== undefined) {
        styles.push('tracking-widest');
      }

      return styles;
    });

    const getLabelStyles = computed(() => {
      const styles = [
        'font-light',
        'leading-4 md:leading-4',
        'text-sm md:text-base',
        'absolute m-4 top-0 left-0',
        'transition duration-150',
      ];

      styles.push(inputFailed.value ? 'text-nuts-red-800' : 'text-neutral-500');
      if (props.alternateBackground) styles.push('gradient-background');

      return styles;
    });

    const handleInputBlur = () => {
      handleBlur();
      let errors;
      if (props.validator && !meta.valid) {
        errors = props.validator();
        setErrors(errors);
      }
      emit('blur', errors);
    };

    const handleInputFocus = () => emit('focus');

    return {
      attributes,
      errorMessage,
      fieldErrorSvg: nutshell['img/field_error.svg'],
      getInputStyles,
      getLabelStyles,
      handleInputBlur,
      handleInputFocus,
      inputFailed,
      inputName,
      validatorFailed,
      value,
    };
  },
});
</script>

<template>
  <div class="relative">
    <input
      v-model="value"
      v-bind="attributes"
      :aria-describedby="`inputHelperText${inputName ? '-' + inputName : ''}`"
      :aria-label="attributes.placeholder"
      :aria-invalid="inputFailed"
      :class="getInputStyles"
      :data-test="`${dataTest ? dataTest + '-' : ''}${attributes.name}`"
      :id="attributes.name"
      @blur="handleInputBlur"
      @focus="handleInputFocus"
    />
    <label
      v-if="showLabel && !showPlaceholder"
      :for="attributes.name"
      class="pointer-events-none floating-label"
      :data-test="`${dataTest ? dataTest + '-' : ''}${attributes.name}-label`"
      :class="getLabelStyles"
    >
      {{ attributes.placeholder }}
    </label>
    <div class="absolute top-0 right-2 py-3 md:right-3.5">
      <transition name="fade">
        <span v-if="$slots.fallbackIcon" key="fallbackIcon">
          <slot name="fallbackIcon" />
        </span>
        <WarningIcon
          v-else-if="inputFailed"
          class="text-nuts-red-800"
          aria-hidden="true"
          :data-test="`${dataTest ? dataTest + '-' : ''}${attributes.name}-field-error-icon`"
          key="fieldError"
        />
      </transition>
    </div>
    <transition name="fade">
      <div
        v-if="!suppressMessaging && (inputFailed || helperText)"
        class="absolute pt-1 pl-px text-xs leading-3"
        :class="
          inputFailed
            ? theme === 'light'
              ? 'text-nuts-red-800'
              : 'text-nuts-red-500'
            : 'text-neutral-500'
        "
        :id="`inputHelperText${inputName ? '-' + inputName : ''}`"
        :role="inputFailed ? 'alert' : undefined"
        :data-test="`${dataTest ? dataTest + '-' : ''}${attributes.name}-validation-error`"
      >
        {{ errorMessage || helperText }}
      </div>
    </transition>
  </div>
</template>

<style>
.placeholder-transparent:-webkit-autofill,
select.placeholder-transparent:valid,
input.placeholder-transparent:not(:placeholder-shown) {
  touch-action: manipulation;
}

.placeholder-transparent:-webkit-autofill + label,
select.placeholder-transparent:valid + label,
input.placeholder-transparent:not(:placeholder-shown) + label {
  transform: translateY(-1.25rem);
  font-size: 0.75rem;
  line-height: 0.625rem;
  background: #fff;
  padding: 0 0.125rem;
}

.placeholder-transparent:-webkit-autofill.gradient-background,
select.placeholder-transparent:valid.gradient-background,
input.placeholder-transparent:not(:placeholder-shown).gradient-background {
  background: linear-gradient(#f5f5f5, #fff);
}
</style>
